import { useState, useCallback } from "react";
import {
  useGetList,
  useCreate,
  useUpdate,
  useDelete,
  useNotify,
  useRefresh,
} from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2, Loader2 } from "lucide-react";

interface LineItem {
  id: string;
  estimate_id: string;
  org_id: string;
  category: "labor" | "material" | "other";
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  line_total: number;
  sort_order: number;
}

interface EstimateLineItemsProps {
  estimateId: string;
  readOnly?: boolean;
}

const CATEGORY_COLORS: Record<string, { color: string; bg: string }> = {
  labor: { color: "#2563eb", bg: "#dbeafe" },
  material: { color: "#16a34a", bg: "#dcfce7" },
  other: { color: "#6b7280", bg: "#f3f4f6" },
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

export function EstimateLineItems({
  estimateId,
  readOnly = false,
}: EstimateLineItemsProps) {
  const { data, isPending } = useGetList<LineItem>("estimate_line_items", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "sort_order", order: "ASC" },
    filter: { estimate_id: estimateId },
  });

  const [create] = useCreate();
  const [update] = useUpdate();
  const [deleteOne] = useDelete();
  const notify = useNotify();
  const refresh = useRefresh();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // New line item form state
  const [newItem, setNewItem] = useState({
    category: "labor" as "labor" | "material" | "other",
    description: "",
    quantity: 1,
    unit: "each",
    unit_price: 0,
  });

  const handleAdd = useCallback(async () => {
    if (!newItem.description.trim()) {
      notify("Description is required", { type: "warning" });
      return;
    }
    setAdding(true);
    try {
      await create(
        "estimate_line_items",
        {
          data: {
            estimate_id: estimateId,
            category: newItem.category,
            description: newItem.description.trim(),
            quantity: newItem.quantity,
            unit: newItem.unit,
            unit_price: newItem.unit_price,
            sort_order: (data?.length ?? 0) + 1,
          },
        },
        { returnPromise: true }
      );
      setNewItem({
        category: "labor",
        description: "",
        quantity: 1,
        unit: "each",
        unit_price: 0,
      });
      refresh();
    } catch (err) {
      notify(`Failed to add line item: ${(err as Error).message}`, {
        type: "error",
      });
    } finally {
      setAdding(false);
    }
  }, [newItem, estimateId, data, create, notify, refresh]);

  const handleDelete = useCallback(
    async (itemId: string) => {
      try {
        await deleteOne(
          "estimate_line_items",
          { id: itemId, previousData: { id: itemId } },
          { returnPromise: true }
        );
        refresh();
      } catch (err) {
        notify(`Delete failed: ${(err as Error).message}`, { type: "error" });
      }
    },
    [deleteOne, notify, refresh]
  );

  const handleInlineUpdate = useCallback(
    async (item: LineItem, field: string, value: unknown) => {
      try {
        await update(
          "estimate_line_items",
          {
            id: item.id,
            data: { [field]: value },
            previousData: item,
          },
          { returnPromise: true }
        );
        setEditingId(null);
        refresh();
      } catch (err) {
        notify(`Update failed: ${(err as Error).message}`, { type: "error" });
      }
    },
    [update, notify, refresh]
  );

  if (isPending) {
    return (
      <div className="text-sm text-muted-foreground py-4">
        Loading line items…
      </div>
    );
  }

  const items = data || [];

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground px-1">
        <div className="col-span-1">Type</div>
        <div className="col-span-4">Description</div>
        <div className="col-span-1 text-right">Qty</div>
        <div className="col-span-1">Unit</div>
        <div className="col-span-2 text-right">Price</div>
        <div className="col-span-2 text-right">Total</div>
        {!readOnly && <div className="col-span-1" />}
      </div>

      {/* Items */}
      {items.map((item) => {
        const catColor = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.other;
        return (
          <Card key={item.id} className="shadow-none">
            <CardContent className="p-2">
              <div className="grid grid-cols-12 gap-2 items-center text-sm">
                <div className="col-span-1">
                  <span
                    className="text-xs font-medium px-1.5 py-0.5 rounded"
                    style={{ color: catColor.color, backgroundColor: catColor.bg }}
                  >
                    {item.category.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="col-span-4 truncate">
                  {editingId === item.id ? (
                    <input
                      type="text"
                      defaultValue={item.description}
                      autoFocus
                      onBlur={(e) =>
                        handleInlineUpdate(item, "description", e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          handleInlineUpdate(
                            item,
                            "description",
                            e.currentTarget.value
                          );
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      className="w-full px-1 py-0.5 border rounded text-sm"
                    />
                  ) : (
                    <span
                      className={!readOnly ? "cursor-pointer hover:underline" : ""}
                      onClick={() => !readOnly && setEditingId(item.id)}
                    >
                      {item.description}
                    </span>
                  )}
                </div>
                <div className="col-span-1 text-right tabular-nums">
                  {item.quantity}
                </div>
                <div className="col-span-1 text-muted-foreground text-xs">
                  {item.unit}
                </div>
                <div className="col-span-2 text-right tabular-nums">
                  {formatCurrency(item.unit_price)}
                </div>
                <div className="col-span-2 text-right font-medium tabular-nums">
                  {formatCurrency(item.line_total)}
                </div>
                {!readOnly && (
                  <div className="col-span-1 text-right">
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Empty state */}
      {items.length === 0 && (
        <div className="text-center py-6 text-sm text-muted-foreground">
          No line items yet. Add labor, materials, or other costs below.
        </div>
      )}

      {/* Add new line item */}
      {!readOnly && (
        <Card className="border-dashed">
          <CardContent className="p-3">
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-2">
                <label className="text-xs text-muted-foreground block mb-1">
                  Type
                </label>
                <select
                  value={newItem.category}
                  onChange={(e) =>
                    setNewItem((n) => ({
                      ...n,
                      category: e.target.value as "labor" | "material" | "other",
                    }))
                  }
                  className="w-full px-2 py-1.5 border rounded text-sm"
                >
                  <option value="labor">Labor</option>
                  <option value="material">Material</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="col-span-3">
                <label className="text-xs text-muted-foreground block mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={newItem.description}
                  onChange={(e) =>
                    setNewItem((n) => ({ ...n, description: e.target.value }))
                  }
                  placeholder="e.g. Framing labor"
                  className="w-full px-2 py-1.5 border rounded text-sm"
                />
              </div>
              <div className="col-span-1">
                <label className="text-xs text-muted-foreground block mb-1">
                  Qty
                </label>
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  value={newItem.quantity}
                  onChange={(e) =>
                    setNewItem((n) => ({
                      ...n,
                      quantity: parseFloat(e.target.value) || 0,
                    }))
                  }
                  className="w-full px-2 py-1.5 border rounded text-sm text-right"
                />
              </div>
              <div className="col-span-1">
                <label className="text-xs text-muted-foreground block mb-1">
                  Unit
                </label>
                <input
                  type="text"
                  value={newItem.unit}
                  onChange={(e) =>
                    setNewItem((n) => ({ ...n, unit: e.target.value }))
                  }
                  className="w-full px-2 py-1.5 border rounded text-sm"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-muted-foreground block mb-1">
                  Unit price
                </label>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={newItem.unit_price}
                  onChange={(e) =>
                    setNewItem((n) => ({
                      ...n,
                      unit_price: parseFloat(e.target.value) || 0,
                    }))
                  }
                  className="w-full px-2 py-1.5 border rounded text-sm text-right"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-muted-foreground block mb-1">
                  &nbsp;
                </label>
                <button
                  onClick={handleAdd}
                  disabled={adding || !newItem.description.trim()}
                  className="w-full flex items-center justify-center gap-1 px-2 py-1.5 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {adding ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Plus className="w-3.5 h-3.5" />
                  )}
                  Add
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
